import React from 'react';
import styled from 'styled-components';
import type { FaxLogRecord } from '@mummssoftware/utils';
import defaults from '../../translations/en.json';

const DetailRoot = styled.div`
  padding: 0.75rem 1rem;
  max-width: 75%;
  animation: fax-detail-content-in 0.24s ease-out;

  @keyframes fax-detail-content-in {
    from {
      opacity: 0;
      transform: translateY(-4px);
    }

    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const Field = styled.div`
  display: flex;
  gap: 6px;
  margin-bottom: 0.35rem;
  min-width: 0;
  text-wrap: wrap;

  &:last-child {
    margin-bottom: 0;
  }
`;

const Label = styled.span`
  font-weight: bold;
  text-decoration: underline;
  white-space: nowrap;
`;

const Value = styled.span`
  white-space: pre-wrap;
  word-break: break-word;
`;

const PdfLink = styled.span`
  font-style: italic;
  color: ${(props) => props.theme.palette.primary.main};
  text-decoration: underline;
  cursor: pointer;
`;

type FaxDetailRendererProps = {
  data: FaxLogRecord;
  onOpenPdf: (row: FaxLogRecord) => void;
};

export const FaxDetailRenderer = ({
  data,
  onOpenPdf,
}: FaxDetailRendererProps) => {
  const fields: Array<{ label: string; value: string | undefined }> = [
    { label: defaults.subject, value: data.coverPageSubject },
    { label: defaults.reference, value: data.coverPageReference },
    { label: defaults.notes, value: data.coverPageNotes },
  ];

  const visibleFields = fields.filter((f) => f.value);

  return (
    <DetailRoot>
      {visibleFields.map(({ label, value }) => (
        <Field key={label}>
          <Label>{`${label}:`}</Label>
          <Value>{value}</Value>
        </Field>
      ))}
      {data.documentUrl && (
        <Field>
          <PdfLink
            role="button"
            tabIndex={0}
            onClick={(e) => {
              e.stopPropagation();
              onOpenPdf(data);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                e.stopPropagation();
                onOpenPdf(data);
              }
            }}
          >
            {defaults.viewPdf}
          </PdfLink>
        </Field>
      )}
    </DetailRoot>
  );
};
